import { useEffect, useMemo, useState } from 'react'
import { useSessionStore } from '@/stores/sessionStore'
import { useStore } from '@/api/stores'
import { usePOSConfig } from '@/api/config'

/**
 * Store-level age restriction ("must be born on or before X").
 *
 * Two independent pieces of BE state drive the ID-check features, exactly
 * as they did in V1 (waio-portal `js/src/controllers/pos/models/state.js`
 * `ageCheck()` and `templates/core/pos/index_3.html`):
 *
 *   * `Store.restriction_age` — the legal age at this store (19 in Ontario,
 *     null/0 at non-restricted stores). Served today by
 *     `/v2/objects/stores/{id}`; `Store.serialize` walks `_meta.fields`, so
 *     the column is already on the wire and needs no BE change. Set in the
 *     portal at Dictionaries → Stores → "Age Restriction".
 *   * `pos.confirmation_of_age_limit` — SysConfig boolean toggled in the
 *     portal at Frontend → Setup → "Confirmation of age limit". Strict
 *     opt-in: an untouched flag arrives as `null`, which means off (see
 *     the long note in api/config.ts). Gates the checkout popup ONLY; the
 *     header banner follows `restriction_age` alone, which matches V1
 *     (index_3.html renders the header line off `store.restriction_dob_str`
 *     with no SysConfig guard).
 *
 * The cutoff date is derived client-side. V1 computed it server-side as
 * `inc_months(now, -12 * restriction_age)` and shipped it as
 * `restriction_dob_str`, but that property is not a model field so it never
 * made it into the serialized store payload. Subtracting N years from today
 * is the same arithmetic, and keeping it on the client means the date rolls
 * over at local midnight instead of going stale in a cached response.
 */
/** Local midnight of the current day — the anchor the cutoff counts back from. */
function startOfLocalDay(): Date {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

export interface AgeRestriction {
  /** Legal age at this store, or null when the store isn't age-restricted. */
  age: number | null
  /** Latest birthdate that still qualifies, or null when unrestricted. */
  cutoffDate: Date | null
  /** e.g. "Aug 19, 2007" — empty string when unrestricted. */
  cutoffLabel: string
  /** True when the checkout ID-check popup should be shown. */
  confirmationEnabled: boolean
  /**
   * False only while the inputs above are genuinely still in flight.
   *
   * Both fields fail OPEN when unknown -- an unresolved store lookup reads
   * as "not age-restricted" and an unresolved config reads as "prompt
   * disabled" -- so a caller that gates on them alone would wave a sale
   * through during the load window. That window is usually nil: the React
   * Query cache is persisted for 30min and PersistQueryClientProvider
   * blocks the first render until it is restored. It opens on a cold start
   * or a cache older than that, where the zustand-persisted cart rehydrates
   * instantly while these two requests are still on the wire.
   *
   * Callers must treat `false` as "don't know yet", not as "no restriction".
   */
  inputsResolved: boolean
}

/**
 * Has this query produced an answer we can act on? Data and a hard error
 * both count. So does `paused` -- React Query parks an `online`-mode query
 * with no cached data when the till is offline, and that never resolves on
 * its own. Treating paused as unresolved would hard-block tendering for the
 * whole outage, which is a worse failure than the one we're guarding
 * against: a till that can't take cash during an internet cut is a till
 * that can't trade at all.
 */
function hasAnswer(query: {
  data: unknown
  isError: boolean
  fetchStatus: string
}): boolean {
  return query.data !== undefined || query.isError || query.fetchStatus === 'paused'
}

export function useAgeRestriction(): AgeRestriction {
  const selectedStore = useSessionStore((s) => s.selectedStore)
  const storeQuery = useStore(selectedStore?.id ?? null)
  const configQuery = usePOSConfig()
  const storeDetails = storeQuery.data
  const posConfig = configQuery.data

  // Both queries are `enabled: !!selectedStore?.id`; with no store selected
  // there is nothing in flight to wait for (and no session to tender on).
  const inputsResolved =
    !selectedStore?.id || (hasAnswer(storeQuery) && hasAnswer(configQuery))

  const restrictionAge = useMemo(() => {
    const raw = storeDetails?.restriction_age
    const n = typeof raw === 'number' ? raw : Number(raw)
    // null / 0 / '' / NaN all mean "not age-restricted" — V1's `ageCheck`
    // short-circuits on the same falsy test.
    return Number.isFinite(n) && n > 0 ? n : null
  }, [storeDetails])

  // Midnight rollover. `restrictionAge` effectively never changes, so a
  // memo keyed on it alone would pin the cutoff to whatever day the hook
  // first ran -- and a till left open overnight would keep showing
  // yesterday's date, which is a day too strict and would turn away a
  // customer who legitimately came of age at midnight. Re-arm a timer at
  // each local midnight and bump a counter so the cutoff advances on its
  // own, without depending on some unrelated render to happen along.
  const [today, setToday] = useState(startOfLocalDay)
  useEffect(() => {
    const now = new Date()
    const nextMidnight = new Date(now)
    nextMidnight.setHours(24, 0, 0, 0)
    // +1s of slack: a timer that fires a hair early would otherwise land
    // back on the same day and re-arm for ~0ms in a tight loop.
    const timer = setTimeout(
      () => setToday(startOfLocalDay()),
      nextMidnight.getTime() - now.getTime() + 1000,
    )
    return () => clearTimeout(timer)
  }, [today])

  const cutoffDate = useMemo(() => {
    if (restrictionAge === null) return null
    const d = new Date(today)
    d.setFullYear(d.getFullYear() - restrictionAge)
    return d
  }, [restrictionAge, today])

  const cutoffLabel = cutoffDate
    ? cutoffDate.toLocaleDateString('en-CA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : ''

  return {
    age: restrictionAge,
    cutoffDate,
    cutoffLabel,
    // Strict opt-in: off unless the portal toggle is explicitly on.
    confirmationEnabled: posConfig?.posConfirmationOfAgeLimit === true,
    inputsResolved,
  }
}
